import React, { useRef } from 'react';
import { graphql, useMutation } from 'react-relay';
import Tooltip from '@atlaskit/tooltip';
import { expVal } from '@atlassian/jira-feature-experiments';
import { type IntlShape, useIntl } from '@atlassian/jira-intl';
import type { IntlShapeV2 } from '@atlassian/jira-intl/src/v2/types.tsx';
import { LabelsPicker } from '@atlassian/jira-label-picker/src/index.tsx';
import InlineErrorMessage from '@atlassian/jira-project-settings-apps-common/src/inline-error-message/view.tsx';
import { preventFormSubmitOnEnter as preventFormSubmit } from '@atlassian/jira-project-settings-apps-common/src/util/form/index.tsx';
import type { ColoredLabel } from '@atlassian/jira-issue-shared-types/src/common/types/labels-type.tsx';
import { useProjectContext } from '@atlassian/jira-providers-project-context/src/index.tsx';
import { useIsAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-admin/index.tsx';
import type { view_labelsUpdateColorMutation } from '@atlassian/jira-relay/src/__generated__/view_labelsUpdateColorMutation.graphql';
import { JiraProjectAri } from '@atlassian/ari/jira/project';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import type { FieldConfigurationComponentProps } from '../../../../types.tsx';
import type { AssociatedField } from '../../../../types/layout-items.tsx';
import type { ValidationError } from '../../../../types/validation.tsx';
import { POSITION_FIELD_DEFAULT_VALUE_INPUT } from '../../../../utils/validation/constants.tsx';
import TestIdWrapper from '../../common/test-wrapper/index.tsx';
import { AkFieldWrapper, FieldConfigurationDefaultValue } from '../../styled.tsx';
import messages from './messages.tsx';

const onDefaultValueChange =
	(field: AssociatedField, onUpdate: (field: AssociatedField) => void) =>
	(labels: string[] | ColoredLabel[]) => {
		if (
			expVal(
				'adding-color-to-labels-field-experiment-milestone2',
				'isColorfulNewLabelsEnabled',
				false,
			)
		) {
			const labelOptions =
				labels.length === 0
					? null
					: labels.map((label) => {
							if (typeof label !== 'string') {
								return label.name;
							}
							return label;
						});

			return onUpdate({
				...field,
				defaultValue: labelOptions && labelOptions.length > 0 ? labelOptions.join(',') : null,
			});
		}
		return onUpdate({
			...field,
			defaultValue: labels.length === 0 ? null : labels.join(','),
		});
	};

export const splitLabels = (labelsString: string | null): string[] => {
	if (labelsString === null) {
		return [];
	}

	return labelsString
		.trim()
		.split(/[\s,]+/)
		.filter((label) => label.length > 0);
};

const getCustomFieldId = (fieldName: string) => {
	const found = fieldName.match('customfield_');
	if (found && found.length > 0) {
		return fieldName.replace('customfield_', '');
	}

	return undefined;
};

type FilteredValidationError = ValidationError & {
	position: typeof POSITION_FIELD_DEFAULT_VALUE_INPUT;
};

function isRelevantError(e: ValidationError): e is FilteredValidationError {
	return e.position === POSITION_FIELD_DEFAULT_VALUE_INPUT;
}

function getFilteredErrors(errors: ReadonlyArray<ValidationError>): FilteredValidationError[] {
	return errors.filter(isRelevantError);
}

const DefaultValueErrors = ({
	filteredErrors,
}: {
	filteredErrors: ReadonlyArray<FilteredValidationError>;
}) => {
	const intl = useIntl();
	if (filteredErrors.length === 0) {
		return null;
	}
	return (
		<>
			{filteredErrors.map((e) => (
				<InlineErrorMessage
					key={e.messageDescriptor.id}
					message={intl.formatMessage(e.messageDescriptor, e.values)}
				/>
			))}
		</>
	);
};

function refineDefaultValue(defaultValue: unknown): ReadonlyArray<string> {
	return splitLabels(typeof defaultValue === 'string' ? defaultValue : null);
}

export const LabelsFieldConfiguration = ({
	field,
	onUpdate,
	fieldErrors,
	tooltipDelay,
	testId,
}: FieldConfigurationComponentProps) => {
	const intl: IntlShape = useIntl();
	// @ts-expect-error LabelsPicker wants IntlShapeV2
	const intlV2: IntlShapeV2 = intl;
	const isModifyDefaultValueDisabled = !field.operations.canModifyDefaultValue;

	const filteredErrors: FilteredValidationError[] = getFilteredErrors(fieldErrors);
	const isInvalid: boolean = filteredErrors.length > 0;

	const defaultValue = refineDefaultValue(field.defaultValue);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const { data: project } = useProjectContext();
	const projectId = project?.projectId;

	const isProjectAdmin = project?.isProjectAdmin;
	const isGlobalAdmin = useIsAdmin();

	const cloudId = useCloudId();

	const lastFocusedKey = useRef<{ key: string; index: number; type: string } | null>(null);

	let projectAriId = null;

	try {
		projectAriId =
			projectId &&
			cloudId &&
			expVal(
				'adding-color-to-labels-field-experiment-milestone2',
				'isColorfulNewLabelsEnabled',
				false,
			) &&
			JiraProjectAri.create({
				siteId: cloudId,
				projectId: projectId.toString(),
			}).toString();
	} catch (error) {
		fireErrorAnalytics({
			meta: {
				id: 'getProjectAriId',
				packageName: 'jiraIssueFieldLabels',
				teamName: 'gryffindor',
			},
			attributes: {
				projectId,
				cloudId,
			},
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			error: error as Error,
			sendToPrivacyUnsafeSplunk: true,
		});
	}

	const [commit] = useMutation<view_labelsUpdateColorMutation>(graphql`
		mutation view_labelsUpdateColorMutation($input: JiraBulkLabelColorUpdateInput!)
		@raw_response_type {
			jira {
				bulkUpdateLabelColor(input: $input) {
					success
					errors {
						message
					}
				}
			}
		}
	`);

	const analyticsEvent = createAnalyticsEvent({});

	return (
		<FieldConfigurationDefaultValue>
			<AkFieldWrapper onKeyDown={preventFormSubmit}>
				<Tooltip
					content={
						isModifyDefaultValueDisabled
							? intl.formatMessage(messages.labelsDefaultValueDisabledTooltip)
							: null
					}
					delay={tooltipDelay}
				>
					<TestIdWrapper testId={testId}>
						<LabelsPicker
							value={defaultValue}
							noPortal
							baseUrl=""
							customFieldId={getCustomFieldId(field.key)}
							isDisabled={isModifyDefaultValueDisabled}
							isRequired={false}
							label={intl.formatMessage(messages.labelsDefaultValueLabel)}
							placeholder={intl.formatMessage(messages.labelsDefaultValuePickerPlaceholder)}
							// @ts-expect-error - TS2769 - No overload matches this call.
							onChange={onDefaultValueChange(field, onUpdate)}
							intl={intlV2}
							enableAnimation={false} // See https://donut-world-dogfood.atlassian.net/browse/MC-1971
							isInvalid={isInvalid}
							{...(expVal(
								'adding-color-to-labels-field-in-jira-experiment',
								'isColoredLabelsEnabled',
								false,
							)
								? { isColored: true }
								: {})}
							{...(expVal(
								'adding-color-to-labels-field-experiment-milestone2',
								'isColorfulNewLabelsEnabled',
								false,
							)
								? {
										isProjectAdmin,
										isGlobalAdmin,
										commit,
										fieldId: field.key,
										projectAriId,
										lastFocusedKey,
										analyticsEvent,
									}
								: {})}
						/>
					</TestIdWrapper>
				</Tooltip>
				<DefaultValueErrors filteredErrors={filteredErrors} />
			</AkFieldWrapper>
		</FieldConfigurationDefaultValue>
	);
};
